import {
  AnalysisScope,
  DataType,
  ValidationSeverity,
  type MessageDefinition,
  type MessageFieldDefinition,
  type ValidationResult,
  type ValidationIssue,
} from '@k-ucap/shared';

export interface FieldValidationResult {
  fieldKey: string;
  status: ValidationSeverity;
  code: string;
  message: string;
  expected?: string;
  actual?: string;
}

const integerTypes = [DataType.UINT8, DataType.INT8, DataType.UINT16, DataType.INT16, DataType.UINT32, DataType.INT32];

const floatTypes = [DataType.FLOAT32, DataType.FLOAT64];

const isIntegerType = (dataType: string): boolean => integerTypes.includes(dataType as DataType);

const isFloatType = (dataType: string): boolean => floatTypes.includes(dataType as DataType);

const integerRanges: Record<string, { min: number; max: number }> = {
  [DataType.UINT8]: { min: 0, max: 255 },
  [DataType.INT8]: { min: -128, max: 127 },
  [DataType.UINT16]: { min: 0, max: 65535 },
  [DataType.INT16]: { min: -32768, max: 32767 },
  [DataType.UINT32]: { min: 0, max: 4294967295 },
  [DataType.INT32]: { min: -2147483648, max: 2147483647 },
};

const validateField = (field: MessageFieldDefinition, value: unknown): FieldValidationResult[] => {
  const issues: FieldValidationResult[] = [];
  const key = field.key;

  const add = (status: ValidationSeverity, code: string, message: string, expected?: string, actual?: string) => {
    issues.push({ fieldKey: key, status, code, message, expected, actual });
  };

  if (value === undefined || value === null || value === '') {
    if (field.required) {
      add(ValidationSeverity.FAIL, 'REQUIRED', `${field.displayName} 필수 값입니다.`);
    }
    return issues;
  }

  if (field.dataType === DataType.BOOLEAN) {
    if (typeof value !== 'boolean') {
      add(
        ValidationSeverity.FAIL,
        'TYPE_BOOLEAN',
        `${field.displayName}는 boolean이어야 합니다.`,
        'boolean',
        String(value)
      );
    }
    return issues;
  }

  if (field.dataType === DataType.STRING) {
    if (typeof value !== 'string') {
      add(
        ValidationSeverity.FAIL,
        'TYPE_STRING',
        `${field.displayName}는 문자열이어야 합니다.`,
        'string',
        String(value)
      );
    }
    return issues;
  }

  if (field.dataType === DataType.ENUM) {
    if (typeof value !== 'string') {
      add(
        ValidationSeverity.FAIL,
        'TYPE_ENUM',
        `${field.displayName}는 enum 문자열이어야 합니다.`,
        'string',
        String(value)
      );
      return issues;
    }
    // enumRef validation: currently permissive, only warn if unknown
    if (!value) {
      add(ValidationSeverity.FAIL, 'ENUM_EMPTY', `${field.displayName} enum 값을 선택하세요.`);
    }
    return issues;
  }

  if (field.dataType === DataType.BYTES) {
    if (!Array.isArray(value)) {
      add(
        ValidationSeverity.FAIL,
        'TYPE_BYTES',
        `${field.displayName}는 byte 배열이어야 합니다.`,
        'number[]',
        String(value)
      );
      return issues;
    }
    const invalid = value.some((v) => typeof v !== 'number' || v < 0 || v > 255 || !Number.isInteger(v));
    if (invalid) {
      add(ValidationSeverity.FAIL, 'BYTES_RANGE', `${field.displayName} byte 값은 0~255 정수여야 합니다.`);
    }
    return issues;
  }

  if (isIntegerType(field.dataType) || isFloatType(field.dataType)) {
    if (typeof value !== 'number' || Number.isNaN(value)) {
      add(ValidationSeverity.FAIL, 'TYPE_NUMBER', `${field.displayName}는 숫자여야 합니다.`, 'number', String(value));
      return issues;
    }
    if (isIntegerType(field.dataType) && !Number.isInteger(value)) {
      add(ValidationSeverity.FAIL, 'TYPE_INTEGER', `${field.displayName}는 정수여야 합니다.`, 'integer', String(value));
    }
    const range = integerRanges[field.dataType];
    if (range && (value < range.min || value > range.max)) {
      add(
        ValidationSeverity.FAIL,
        'INTEGER_RANGE',
        `${field.displayName}는 ${range.min}~${range.max} 범위여야 합니다.`,
        `${range.min}~${range.max}`,
        String(value)
      );
    }
    if (field.minimum !== undefined && value < field.minimum) {
      add(
        ValidationSeverity.FAIL,
        'MINIMUM',
        `${field.displayName} 최소값 ${field.minimum}을(를) 충족해야 합니다.`,
        `>= ${field.minimum}`,
        String(value)
      );
    }
    if (field.maximum !== undefined && value > field.maximum) {
      add(
        ValidationSeverity.FAIL,
        'MAXIMUM',
        `${field.displayName} 최대값 ${field.maximum}을(를) 초과할 수 없습니다.`,
        `<= ${field.maximum}`,
        String(value)
      );
    }
    return issues;
  }

  // Unknown data type: warn but do not fail
  add(
    ValidationSeverity.WARN,
    'UNKNOWN_DATA_TYPE',
    `${field.displayName}의 dataType ${field.dataType}은(는) PoC에서 검증되지 않았습니다.`
  );
  return issues;
};

export class MessageValidationService {
  validate(message: MessageDefinition, payload: Record<string, unknown>): ValidationResult {
    const issues: ValidationIssue[] = [];

    if (!message.supported) {
      issues.push({
        severity: ValidationSeverity.WARN,
        code: 'UNSUPPORTED_MESSAGE',
        message: `${message.name}은(는) PoC에서 지원되지 않는 메시지입니다.`,
        path: 'message',
        scope: AnalysisScope.DEFINITION,
      });
    }

    for (const field of message.fields) {
      const value = payload[field.key];
      const fieldIssues = validateField(field, value);
      issues.push(
        ...fieldIssues.map((fi) => ({
          severity: fi.status,
          code: fi.code,
          message: fi.message,
          path: fi.fieldKey,
          scope: AnalysisScope.FIELD,
          expected: fi.expected,
          actual: fi.actual,
        }))
      );
    }

    const knownKeys = new Set(message.fields.map((f) => f.key));
    for (const key of Object.keys(payload)) {
      if (!knownKeys.has(key)) {
        issues.push({
          severity: ValidationSeverity.WARN,
          code: 'UNKNOWN_FIELD',
          message: `Payload에 정의되지 않은 필드 ${key}가 존재합니다.`,
          path: key,
          scope: AnalysisScope.FIELD,
        });
      }
    }

    const severity = this.aggregateSeverity(issues);
    return {
      severity,
      issues,
      timestamp: new Date().toISOString(),
    };
  }

  private aggregateSeverity(issues: ValidationIssue[]): ValidationSeverity {
    if (issues.some((i) => i.severity === ValidationSeverity.FAIL)) return ValidationSeverity.FAIL;
    if (issues.some((i) => i.severity === ValidationSeverity.WARN)) return ValidationSeverity.WARN;
    if (issues.some((i) => i.severity === ValidationSeverity.INFO)) return ValidationSeverity.INFO;
    return ValidationSeverity.PASS;
  }
}

export const createMessageValidationService = (): MessageValidationService => new MessageValidationService();
